import CancelOutlined from '@mui/icons-material/CancelOutlined';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import type SvgIcon from '@mui/material/SvgIcon';
import { useCallback } from 'react';
import { TypeOptions, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.min.css';

export enum ToastType {
    MESSAGE = 'message',
    ALERT = 'alert',
}

// Maps each toast to a react-toastify type so it shares the theme-driven type colors in `src/toast.tsx`.
const TOAST_TYPES: Record<ToastType, TypeOptions> = {
    [ToastType.MESSAGE]: 'success',
    [ToastType.ALERT]: 'error',
};

export type ToastOptions = {
    hideIcon?: boolean;
    hideCloseButton?: boolean;
    autoClose?: number | false;
    deduplicate?: boolean;
    icon?: typeof SvgIcon;
};

const genericToast = (
    toastType: ToastType = ToastType.MESSAGE,
    message?: string,
    options: ToastOptions = {},
) => {
    const IconComponent = options.icon ?? InfoOutlined;
    toast(message, {
        type: TOAST_TYPES[toastType],
        closeButton: !options.hideCloseButton,
        autoClose: options.autoClose ?? 3000,
        data: {
            toastType,
            message,
        },
        icon: options.hideIcon ? false : <IconComponent sx={{ height: 20, width: 20 }} />,
        className: 'newToast',
        ...(options.deduplicate !== false && { toastId: [toastType, message].join(',') }),
    });
};

export const useToast = () => {
    const showMessage = useCallback((message: string, options?: ToastOptions) => {
        genericToast(ToastType.MESSAGE, message, {
            ...options,
            icon: options?.icon ?? InfoOutlined,
        });
    }, []);

    const showAlert = useCallback((message: string, options?: ToastOptions) => {
        genericToast(ToastType.ALERT, message, {
            ...options,
            icon: options?.icon ?? CancelOutlined,
        });
    }, []);

    return {
        showMessage,
        showAlert,
    };
};
